import { registerPlugin, WebPlugin } from '@capacitor/core';

import { baueFrameDeps, zieheFramesImBrowser, type FrameWebDeps } from './videoFramesWeb';

/**
 * Der Vorschaustreifen unter dem Video-Annotationseditor: eine Handvoll Einzelbilder, die eine
 * ungefähre Ahnung geben, wann was zu sehen ist.
 *
 * **Nativ und nicht im WebView** — aus demselben Grund wie `VideoThumbnail.swift`: der Web-Teil
 * müsste die ganze Datei als Datenadresse durch den Speicher des WebViews schieben und auf ein
 * Canvas zeichnen, und auf iOS 15 ist genau das die Stelle, an der es knapp wird.
 * `AVAssetImageGenerator` liest nur die zehn Bilder.
 *
 * ⚠️ **Die Bilder sind Anzeigehilfe, kein Beleg.** Sie tragen keine Beglaubigung, zu ihnen
 * gehört keine Protokollzeile, und sie liegen deshalb **ausserhalb** von `logs/`. Lägen sie
 * neben der Aufnahme, müsste `domain/abgeleitet.ts` sie kennen und `server/anhaenge.ts` sie
 * wieder aussortieren — jede dieser Listen wäre eine weitere Kopie desselben Wissens.
 */

/**
 * Wie viele Vorschauen der Streifen trägt.
 *
 * Zehn nebeneinander, fest — Entscheidung des Nutzers vom 2026-09-20. Kein Nachladen, kein
 * Sichtfenster: der Streifen soll eine Ahnung geben, nicht jedes Einzelbild zeigen.
 */
export const FRAME_ANZAHL = 10;

/** Wo die Vorschauen einer Aufnahme liegen — unterhalb von Documents, ohne führenden Schrägstrich. */
export const frameOrdner = (videoPfad: string) =>
  'cache/frames/' + videoPfad.slice(videoPfad.lastIndexOf('/') + 1);

/**
 * Zu welchen Zeitpunkten die Vorschauen gezogen werden — die **Mitte** jedes Abschnitts.
 *
 * ⚠️ **Nicht bei 0 anfangen.** Eine Aufnahme beginnt regelmässig mit einem schwarzen oder
 * unscharfen Bild, weil die Kamera noch belichtet; `VideoThumbnail.swift` umgeht dieselbe Falle
 * mit seiner halben Sekunde. Die Mitte trifft ausserdem bei jedem Abschnitt das, was ihn
 * ausmacht, statt seinen Übergang aus dem vorigen.
 */
export function frameZeiten(durationMs: number, anzahl: number): number[] {
  const abschnitt = durationMs / anzahl;
  return Array.from({ length: anzahl }, (_, i) => Math.floor((i + 0.5) * abschnitt));
}

export interface VideoFramesPlugin {
  /**
   * Zieht `msList.length` Einzelbilder aus der Aufnahme und legt sie in `ordner` ab.
   * Gibt die Pfade in derselben Reihenfolge zurück; ein nicht lesbarer Zeitpunkt fehlt.
   */
  extract(options: {
    /** Pfad der Aufnahme unterhalb von Documents. */
    path: string;
    /** Zielordner unterhalb von Documents (`frameOrdner`). */
    ordner: string;
    msList: number[];
    /** Breite der Vorschauen in Pixeln; die Höhe folgt dem Seitenverhältnis. */
    breite: number;
  }): Promise<{ pfade: string[] }>;
}

/**
 * Im Browser über `<video>` und `<canvas>` — s. `platform/videoFramesWeb.ts`.
 *
 * ⚠️ **Bis zum 2026-09-22 warf dieser Rückfall.** Die Begründung oben gilt für das **Gerät**:
 * dort wäre der Umweg über den Speicher des WebViews auf iOS 15 zu teuer. Am Schreibtisch
 * gibt es diese Enge nicht, und ohne Streifen wäre der Annotationseditor im Browser halb da.
 */
export class VideoFramesWeb extends WebPlugin implements VideoFramesPlugin {
  private readonly deps: Required<FrameWebDeps>;

  constructor(deps: FrameWebDeps = {}) {
    super();
    this.deps = baueFrameDeps(deps);
  }

  async extract(options: {
    path: string;
    ordner: string;
    msList: number[];
    breite: number;
  }): Promise<{ pfade: string[] }> {
    return zieheFramesImBrowser(options, this.deps);
  }
}

export const VideoFrames = registerPlugin<VideoFramesPlugin>('VideoFrames', {
  web: () => new VideoFramesWeb(),
});
